import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { SmsAlertsPrompt } from '../components/SmsAlertsPrompt';
import { Loading } from '../components/ui';
import { useAuth } from '../store/auth';
import { ForgotPage, LoginPage, RegisterPage, VerifyPhonePage } from './auth';
import { LobbyPage } from './Lobby';
import { STANDALONE } from '../platform';
import { StandaloneHome } from './StandaloneHome';

const MatchLayout = lazy(() => import('./match/MatchLayout'));
const LobbyRoom = lazy(() => import('./LobbyRoom'));
const MapScreen = lazy(() => import('./match/MapScreen'));
const TerritoriesScreen = lazy(() => import('./match/TerritoriesScreen'));
const OrdersScreen = lazy(() => import('./match/OrdersScreen'));
const ResearchScreen = lazy(() => import('./match/ResearchScreen'));
const FleetScreen = lazy(() => import('./match/FleetScreen'));
const MarketScreen = lazy(() => import('./match/MarketScreen'));
const LogisticsScreen = lazy(() => import('./match/LogisticsScreen'));
const IntelScreen = lazy(() => import('./match/IntelScreen'));
const DiplomacyScreen = lazy(() => import('./match/DiplomacyScreen'));
const NewsScreen = lazy(() => import('./match/NewsScreen'));
const ScoreScreen = lazy(() => import('./match/ScoreScreen'));
const GovernmentScreen = lazy(() => import('./match/GovernmentScreen'));
const SimulatorScreen = lazy(() => import('./SimulatorScreen'));
const WikiScreen = lazy(() => import('./WikiScreen'));
const AdminScreen = lazy(() => import('./AdminScreen'));
const TownEditorScreen = lazy(() => import('./admin/TownEditorScreen'));
const AccountScreen = lazy(() => import('./AccountScreen'));

function RequireAuth({ children }: { children: ReactNode }) {
  const { ready, user } = useAuth();
  const location = useLocation();
  if (!ready) return <Loading />;
  if (!user) return <Navigate to="/entrar" replace state={{ from: location.pathname }} />;
  return <>{children}</>;
}

export function App() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {!STANDALONE && (
          <>
            <Route path="/entrar" element={<LoginPage />} />
            <Route path="/cadastro" element={<RegisterPage />} />
            <Route
              path="/confirmar-celular"
              element={
                <RequireAuth>
                  <VerifyPhonePage />
                </RequireAuth>
              }
            />
            <Route path="/esqueci-senha" element={<ForgotPage />} />
          </>
        )}
        <Route
          path="/"
          element={<RequireAuth>{STANDALONE ? <StandaloneHome /> : <LobbyPage />}</RequireAuth>}
        />
        <Route
          path="/simulador"
          element={
            <RequireAuth>
              <SimulatorScreen />
            </RequireAuth>
          }
        />
        <Route
          path="/wiki"
          element={
            <RequireAuth>
              <WikiScreen />
            </RequireAuth>
          }
        />
        {!STANDALONE && (
          <>
            <Route
              path="/admin"
              element={
                <RequireAuth>
                  <AdminScreen />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/cidades/:tid?"
              element={
                <RequireAuth>
                  <TownEditorScreen />
                </RequireAuth>
              }
            />
            <Route
              path="/conta"
              element={
                <RequireAuth>
                  <AccountScreen />
                </RequireAuth>
              }
            />
          </>
        )}
        <Route
          path="/partidas/:id/lobby"
          element={
            <RequireAuth>
              <LobbyRoom />
            </RequireAuth>
          }
        />
        <Route
          path="/partidas/:id"
          element={
            <RequireAuth>
              <MatchLayout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="mapa" replace />} />
          <Route path="mapa" element={<MapScreen />} />
          <Route path="territorios" element={<TerritoriesScreen />} />
          <Route path="territorios/:tid" element={<TerritoriesScreen />} />
          <Route path="ordens" element={<OrdersScreen />} />
          <Route path="frota" element={<FleetScreen />} />
          <Route path="bolsa" element={<MarketScreen />} />
          <Route path="pesquisa" element={<ResearchScreen />} />
          <Route path="logistica" element={<LogisticsScreen />} />
          <Route path="inteligencia" element={<IntelScreen />} />
          <Route path="diplomacia" element={<DiplomacyScreen />} />
          <Route path="noticias" element={<NewsScreen />} />
          <Route path="placar" element={<ScoreScreen />} />
          <Route path="governo" element={<GovernmentScreen />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!STANDALONE && <SmsAlertsPrompt />}
    </Suspense>
  );
}
